import { Cart2 } from "../modules/cart2/type";

// *********************
// Basic
// *********************

export type WSEvent = WSClientEvent | WSServerEvent;

export type WSEventName = WSClientEventName | WSServerEventName;

// *********************
// Client
// *********************

export enum WSClientEventName {
    RESULT = 'RESULT',
    IS_CART_EXISTED = 'IS_CART_EXISTED',
    INIT_CART = 'INIT_CART',
    CONNECT_CART = 'CONNECT_CART',
    UPDATE_CART = 'UPDATE_CART',
    DISCONNECT_CART = 'DISCONNECT_CART',
    TO_NEXT_TURN = 'TO_NEXT_TURN',
    SUBMIT_CART = 'SUBMIT_CART',
    MARKETING = 'MARKETING',
    CART_ERROR = 'CART_ERROR',
    CHANGE_CART_TABLE = 'CHANGE_CART_TABLE',
    FIRST_EXCESS_WARNING = 'FIRST_EXCESS_WARNING',
}

export type REClient = {
    'RESULT': Result;
    'IS_CART_EXISTED': IsCartExisted;
    'INIT_CART': InitCart;
    'CONNECT_CART': ConnectCart;
    'UPDATE_CART': UpdateCart;
    'DISCONNECT_CART': DisconnectCart;
    'TO_NEXT_TURN': ToNextTurn;
    'SUBMIT_CART': SubmitCart;
    'MARKETING': Marketing;
    'CART_ERROR': CartError;
    'CHANGE_CART_TABLE': ChangeCartTable;
}

export type WSClientEvent = Result | IsCartExisted | InitCart | ConnectCart | UpdateCart | DisconnectCart | ToNextTurn | SubmitCart | Marketing | CartError;

export type Result = {
    eid: string;
    type: WSClientEventName.RESULT;
    event: string;
    statusCode: number;
    details?: Record<string, unknown>
};

export type IsCartExisted = {
    type: WSClientEventName.IS_CART_EXISTED,
    date: Date;
    data: boolean;
    parameter: unknown;
}

export type InitCart = {
    type: WSClientEventName.INIT_CART,
    date: Date;
    data: Cart2.Cart;
    parameter: unknown;
}

export type ConnectCart = {
    type: WSClientEventName.CONNECT_CART;
    date: Date;
    data: Cart2.Cart;
    user: Partial<Cart2.User>;
    project: Cart2.Project;
};

export type UpdateCart = {
    type: WSClientEventName.UPDATE_CART;
    date: Date;
    data: Cart2.Cart;
    parameter: unknown;
};

export type DisconnectCart = {
    type: WSClientEventName.DISCONNECT_CART;
    date: Date;
    data: Cart2.Cart;
    user: Partial<Cart2.User>;
    project: Cart2.Project;
};

export type ToNextTurn = {
    type: WSClientEventName.TO_NEXT_TURN;
    date: Date;
    data: Cart2.Cart;
    parameter: unknown;
};

export type SubmitCart = {
    type: WSClientEventName.SUBMIT_CART;
    date: Date;
    data: Cart2.Cart;
    toNextTurn: boolean;
    user: Partial<Cart2.User>;
    project: Cart2.Project;
    socketId: string;
    requestKey: string;
}

export type Marketing = {
    type: WSClientEventName.MARKETING;
    date: Date;
    data: Cart2.CheckedMarketingResult;
    parameter: unknown;
};

export type CartError = {
    message: string,
    serverEventName: WSServerEventName,
    detail: any
};

export type ChangeCartTable = {
    type: WSClientEventName.CHANGE_CART_TABLE;
    date: Date;
    data: Cart2.CheckedMarketingResult;
    parameter: unknown;
};

export type ExcessTip = {
    date: string;
    type: WSClientEventName.FIRST_EXCESS_WARNING;
    isAddedToCart: boolean;
}


// *********************
// Server
// *********************

export enum WSServerEventName {
    // 判断购物车是否存在
    CART2_EXISTED = '/cart2/existed',
    // 初始化购物车
    CART2_INIT = '/cart2/init',
    // 连接购物车
    CART2_CONNECT = '/cart2/connect',
    // 连出购物车
    CART2_DISCONNECT = '/cart2/disconnect',
    // 初始化购物车过滤器
    CART2_FILTER_INIT = '/cart2/filter/init',
    // 修改购物车信息
    CART2_UPDATE = '/cart2/update',
    // 删除购物车
    CART2_DELETE = '/cart2/delete',
    // 推进轮次
    CART2_TURN_NEXT = '/cart2/turn/next',
    // 提交购物车
    CART2_SUBMIT = '/cart2/submit',
    // 清空购物车
    CART2_ITEM_DELETE = '/cart2/item/delete'
}

export type REService = {
    'CART2_EXISTED': Cart2Existed;
    'CART2_INIT': Cart2Init,
    'CART2_CONNECT': Cart2Connect,
    'CART2_DISCONNECT': Cart2Disconnect,
    'CART2_FILTER_INIT': Cart2FilterInit,
    'CART2_UPDATE': Cart2Update,
    'CART2_DELETE': Cart2Delete,
    'CART2_TURN_NEXT': Cart2TurnNext,
    'CART2_SUBMIT': Cart2Submit,
    'CART2_ITEM_DELETE': Cart2ItemDelete
}

export type WSServerEvent = Cart2Existed | Cart2Init | Cart2Connect | Cart2Disconnect | Cart2FilterInit | Cart2Update | Cart2Delete | Cart2TurnNext | Cart2Submit | Cart2ItemDelete;

export type Cart2Existed = {
    event: WSServerEventName.CART2_EXISTED;
    param: {
        cartFind: Cart2.CartFind,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Init = {
    event: WSServerEventName.CART2_INIT;
    param: {
        cartFind: Cart2.CartFind,
        customerNum?: number,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Connect = {
    event: WSServerEventName.CART2_CONNECT;
    param: {
        cartFind: Cart2.CartFind,
        user: Partial<Cart2.User>,
        project: Cart2.Project,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Disconnect = {
    event: WSServerEventName.CART2_DISCONNECT;
    param: {
        cartFind: Cart2.CartFind,
        user: Cart2.User,
        project: Cart2.Project,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2FilterInit = {
    event: WSServerEventName.CART2_FILTER_INIT;
    param: {
        projects?: Cart2.Project[],
        turns?: number[],
        userIds?: string[],
        userNames?: string[],
        userTypes?: Cart2.UserType[],
        groupBys?: Cart2.GroupBy[],
        sortBys?: Cart2.SortBy[],
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Update = {
    event: WSServerEventName.CART2_UPDATE;
    param: {
        cartFind: Cart2.CartFind,
        itemTarget: Cart2.ItemTarget & {
            menuItem?: Partial<Cart2.Item>
        },
        action: Cart2.CartAction,
        marketingConfig: Cart2.MarketingConfig,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Delete = {
    event: WSServerEventName.CART2_DELETE;
    param: {
        cartFind: Cart2.CartFind,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2TurnNext = {
    event: WSServerEventName.CART2_TURN_NEXT;
    param: {
        cartFind: Cart2.CartFind,
        operation?: {
            withItem?: {
                itemFind: Cart2.ItemFind,
                domain: Cart2.ActionDomain
            }
        }
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2Submit = {
    event: WSServerEventName.CART2_SUBMIT;
    param: {
        cartFind: Cart2.CartFind,
        itemsInfo: {
            id: string,
            ruleConfig: Cart2.RuleConfig,
            [key: string]: any
        }[],
        orderInfo: Cart2.OrderInfo,
        user: Cart2.User,
        project: Cart2.Project,
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

export type Cart2ItemDelete = {
    event: WSServerEventName.CART2_ITEM_DELETE;
    param: {
        cartFind: Cart2.CartFind,
        itemsInfo: {
            id: string,
            ruleConfig: Partial<Cart2.RuleConfig>
        }[],
        eid?: string
    };
    option?: WayOfCommunicationType;
    callback?: (data: Result) => Promise<void>;
}

// **********************************
// Socket.io Event
// **********************************

export enum SocketIoEventName {
    ERROR = 'error',
    RECONNECT = 'reconnect',
    RECONNECT_ATTEMPT = 'reconnect_attempt',
    RECONNECT_ERROR = 'reconnect_error',
    RECONNECT_FAILED = 'reconnect_failed',
    PING = 'ping',
    VOLATILE = 'volatile',
    CONNECT = 'connect',
    DISCONNECT = 'disconnect',
    CONNECT_ERROR = 'connect_error '
}

// **********************************
// Communication Type
// **********************************

export enum WayOfCommunicationType {
    DEFAULT = 'DEFAULT',
    SYNCHRONOUS = 'SYNCHRONOUS'
}